import React, { createContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import { clearAgents } from '../components/TradeController/hyperliquidUtils';
import { getInjectedProvider } from '../utils/injectedProvider';

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

const cgProxy = (path, params) => {
  const qs = new URLSearchParams({ path, ...params }).toString();
  return axios.get(`${API_BASE}/proxy/coingecko?${qs}`, { timeout: 10000 });
};

// Create Wallet Context
export const WalletContext = createContext();

// Chain configurations
const CHAINS = {
  1: {
    name: 'Ethereum',
    shortName: 'ETH',
    rpcUrls: ['https://mainnet.infura.io/v3/', 'https://eth.llamarpc.com', 'https://rpc.ankr.com/eth'],
    nativeCurrency: { symbol: 'ETH', decimals: 18 },
    alchemyNetwork: 'eth-mainnet',
    blockscoutUrl: 'https://eth.blockscout.com'
  },
  10: {
    name: 'Optimism',
    shortName: 'OP',
    rpcUrls: ['https://optimism-mainnet.infura.io/v3/', 'https://mainnet.optimism.io', 'https://rpc.ankr.com/optimism'],
    nativeCurrency: { symbol: 'ETH', decimals: 18 },
    alchemyNetwork: 'opt-mainnet',
    blockscoutUrl: 'https://optimism.blockscout.com'
  },
  42161: {
    name: 'Arbitrum',
    shortName: 'ARB',
    rpcUrls: ['https://arb1.arbitrum.io/rpc', 'https://rpc.ankr.com/arbitrum'],
    nativeCurrency: { symbol: 'ETH', decimals: 18 },
    alchemyNetwork: 'arb-mainnet',
    blockscoutUrl: 'https://arbitrum.blockscout.com'
  },
  8453: {
    name: 'Base',
    shortName: 'BASE',
    rpcUrls: ['https://base-mainnet.infura.io/v3/', 'https://base.llamarpc.com', 'https://rpc.ankr.com/base'],
    nativeCurrency: { symbol: 'ETH', decimals: 18 },
    alchemyNetwork: 'base-mainnet',
    blockscoutUrl: 'https://base.blockscout.com'
  },
  421614: {
    name: 'Arbitrum Sepolia',
    shortName: 'ARB-SEP',
    rpcUrls: ['https://sepolia-rollup.arbitrum.io/rpc'],
    nativeCurrency: { symbol: 'ETH', decimals: 18 },
    alchemyNetwork: null,
    blockscoutUrl: 'https://sepolia.arbiscan.io'
  }
};

// RPC Provider fallback chain - supports multiple chains
const getRPCProviders = (chainId = 1) => {
  const infuraId = process.env.REACT_APP_INFURA_PROJECT_ID;
  const chain = CHAINS[chainId];

  if (!chain) {
    console.warn(`[WalletContext] Unknown chain ID: ${chainId}`);
    return [];
  }

  const providers = [];

  // Add Infura RPC if available (for supported chains)
  if (infuraId && infuraId !== 'your_infura_project_id_here') {
    const infuraRpc = chain.rpcUrls[0];
    if (infuraRpc.includes('infura.io')) {
      providers.push(infuraRpc + infuraId);
    }
  }

  // Add public fallback RPCs
  for (const rpcUrl of chain.rpcUrls) {
    if (!rpcUrl.includes('infura.io')) {
      providers.push(rpcUrl);
    }
  }

  return providers;
};

// Create provider for specific chain
const createChainProvider = async (chainId) => {
  const rpcUrls = getRPCProviders(chainId);

  for (const rpcUrl of rpcUrls) {
    try {
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      // Test connection
      await provider.getBlockNumber();
      console.log(`[WalletContext] Connected to ${CHAINS[chainId].name} via ${rpcUrl}`);
      return provider;
    } catch (error) {
      console.warn(`[WalletContext] Failed to connect to ${rpcUrl}:`, error.message);
      continue;
    }
  }

  throw new Error(`Failed to connect to any RPC for chain ${chainId}`);
};

// Get cached token metadata from localStorage
const getCachedMetadata = (address, chainId) => {
  try {
    const cacheKey = `xyr_token_metadata_${chainId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const age = Date.now() - timestamp;

      // Use cache if less than 7 days old
      if (age < 7 * 24 * 60 * 60 * 1000) {
        return data[address.toLowerCase()] || null;
      }
    }
  } catch (e) {
    console.warn('[WalletContext] Cache read error:', e.message);
  }
  return null;
};

// Save token metadata to localStorage cache
const cacheMetadata = (address, chainId, metadata) => {
  try {
    const cacheKey = `xyr_token_metadata_${chainId}`;
    let cache = { data: {}, timestamp: Date.now() };

    const existing = localStorage.getItem(cacheKey);
    if (existing) {
      cache = JSON.parse(existing);
    }

    cache.data[address.toLowerCase()] = metadata;
    cache.timestamp = Date.now();

    localStorage.setItem(cacheKey, JSON.stringify(cache));
  } catch (e) {
    console.warn('[WalletContext] Cache write error:', e.message);
  }
};

// Fetch ERC-20 tokens from Blockscout (free, no API key needed)
const fetchTokensFromBlockscout = async (address, chainId) => {
  const chain = CHAINS[chainId];
  if (!chain || !chain.blockscoutUrl) return [];

  try {
    console.log(`[WalletContext] Fetching tokens from Blockscout for ${chain.name}...`);
    const response = await axios.get(
      `${chain.blockscoutUrl}/api/v2/addresses/${address}/tokens?type=ERC-20`,
      { timeout: 10000 }
    );
    const items = response.data.items || [];
    const tokens = [];

    for (const item of items) {
      const token = item.token || {};
      // Blockscout uses address_hash, not address
      const contractAddress = token.address_hash || token.address;
      const decimals = token.decimals != null ? parseInt(token.decimals) : 18;
      const symbol = token.symbol || 'UNKNOWN';
      const rawBalance = item.value || '0';
      // Blockscout provides exchange_rate (USD price) directly
      const usdPrice = token.exchange_rate ? parseFloat(token.exchange_rate) : null;

      const balanceFormatted = parseFloat(
        ethers.utils.formatUnits(rawBalance, decimals)
      );

      const isDust = balanceFormatted < 0.000001;
      const isSpam = (
        symbol.toLowerCase().includes('claim') ||
        symbol.toLowerCase().includes('visit') ||
        symbol.includes('http') ||
        symbol.includes('.com') ||
        symbol.includes('$') ||
        /[^\x00-\x7F]/.test(symbol)
      );

      if (balanceFormatted > 0 && !isDust && !isSpam) {
        tokens.push({
          chain: chain.name,
          chainId,
          symbol,
          address: contractAddress,
          balance: balanceFormatted,
          decimals,
          type: 'erc20',
          name: token.name || symbol,
          logo: token.icon_url || null,
          blockscoutPrice: usdPrice  // carry price from Blockscout directly
        });
        console.log(`[WalletContext] Blockscout ${chain.name} ${symbol}: ${balanceFormatted} @ $${usdPrice}`);
      }
    }

    console.log(`[WalletContext] Blockscout found ${tokens.length} tokens on ${chain.name}`);
    return tokens;
  } catch (error) {
    console.warn(`[WalletContext] Blockscout failed for ${chain.name}:`, error.message);
    return [];
  }
};

// Fetch wallet's first-ever transaction date across all chains
const fetchWalletFirstTxDate = async (address) => {
  const alchemyApiKey = process.env.REACT_APP_ALCHEMY_API_KEY;
  if (!alchemyApiKey) return null;

  let earliest = null;
  await Promise.all(Object.values(CHAINS).map(async (chain) => {
    if (!chain.alchemyNetwork) return;
    try {
      const url = `https://${chain.alchemyNetwork}.g.alchemy.com/v2/${alchemyApiKey}`;
      // Check both sent and received transfers
      const [sent, received] = await Promise.all([
        axios.post(url, { id: 1, jsonrpc: '2.0', method: 'alchemy_getAssetTransfers',
          params: [{ fromBlock: '0x0', fromAddress: address, order: 'asc', maxCount: '0x1',
            withMetadata: true, category: ['external', 'erc20', 'erc721'] }]
        }, { timeout: 8000 }),
        axios.post(url, { id: 1, jsonrpc: '2.0', method: 'alchemy_getAssetTransfers',
          params: [{ fromBlock: '0x0', toAddress: address, order: 'asc', maxCount: '0x1',
            withMetadata: true, category: ['external', 'erc20', 'erc721'] }]
        }, { timeout: 8000 }),
      ]);
      for (const resp of [sent, received]) {
        const ts = resp.data?.result?.transfers?.[0]?.metadata?.blockTimestamp;
        if (ts) {
          const d = new Date(ts);
          if (!earliest || d < earliest) earliest = d;
        }
      }
    } catch (e) { /* skip */ }
  }));
  return earliest;
};

const ERC20_ABI = [
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)'
];

// Fetch ALL ERC-20 tokens from Alchemy API (OPTIMIZED with batching and caching)
const fetchAllTokensFromAlchemy = async (address, chainId) => {
  const alchemyApiKey = process.env.REACT_APP_ALCHEMY_API_KEY;

  if (!alchemyApiKey || alchemyApiKey === '') {
    console.log('[WalletContext] No Alchemy API key configured, skipping token fetch');
    return [];
  }

  const chain = CHAINS[chainId];
  if (!chain || !chain.alchemyNetwork) {
    console.warn(`[WalletContext] No Alchemy network configured for chain ${chainId}`);
    return [];
  }

  try {
    const url = `https://${chain.alchemyNetwork}.g.alchemy.com/v2/${alchemyApiKey}`;

    console.log(`[WalletContext] Fetching all ERC-20 tokens from Alchemy for ${chain.name}...`);

    // Get token balances using Alchemy API
    const response = await axios.post(url, {
      jsonrpc: '2.0',
      id: 1,
      method: 'alchemy_getTokenBalances',
      params: [address, 'erc20']
    }, { timeout: 10000 });

    if (!response.data || !response.data.result || !response.data.result.tokenBalances) {
      return [];
    }

    const tokenBalances = response.data.result.tokenBalances;

    // Filter out zero balances FIRST
    const nonZeroBalances = tokenBalances.filter(tb => {
      const balance = tb.tokenBalance;
      return balance && balance !== '0x0' && balance !== '0x';
    });

    if (nonZeroBalances.length === 0) {
      console.log(`[WalletContext] No tokens found on ${chain.name}`);
      return [];
    }

    console.log(`[WalletContext] Found ${nonZeroBalances.length} tokens with non-zero balance on ${chain.name}`);

    // Check cache for each token, collect uncached addresses
    const uncachedAddresses = [];
    const cachedMetadata = {};

    for (const tb of nonZeroBalances) {
      const cached = getCachedMetadata(tb.contractAddress, chainId);
      if (cached) {
        cachedMetadata[tb.contractAddress.toLowerCase()] = cached;
      } else {
        uncachedAddresses.push(tb.contractAddress);
      }
    }

    console.log(`[WalletContext] ${Object.keys(cachedMetadata).length} tokens in cache, ${uncachedAddresses.length} need fetching`);

    // Batch fetch metadata for uncached tokens using Alchemy batch JSON-RPC
    const fetchedMetadata = {};

    if (uncachedAddresses.length > 0) {
      // Batch requests (max 50 per batch - Alchemy supports this easily)
      const batchSize = 50;
      for (let i = 0; i < uncachedAddresses.length; i += batchSize) {
        const batch = uncachedAddresses.slice(i, i + batchSize);

        // Create batch request
        const batchRequests = batch.map((addr, idx) => ({
          jsonrpc: '2.0',
          id: i + idx,
          method: 'alchemy_getTokenMetadata',
          params: [addr]
        }));

        try {
          const batchResponse = await axios.post(url, batchRequests, { timeout: 5000 });

          // Process batch response
          if (Array.isArray(batchResponse.data)) {
            batchResponse.data.forEach((res, idx) => {
              if (res.result) {
                const addr = batch[idx];
                fetchedMetadata[addr.toLowerCase()] = res.result;
                // Cache this metadata
                cacheMetadata(addr, chainId, res.result);
              }
            });
          }
        } catch (error) {
          console.warn(`[WalletContext] Batch metadata fetch error for batch ${i / batchSize + 1}:`, error.message);
        }
      }
    }

    // Combine cached and fetched metadata
    const allMetadata = { ...cachedMetadata, ...fetchedMetadata };

    // Fallback: directly read metadata from contract for any token still missing it
    const missingMeta = nonZeroBalances.filter(tb => !allMetadata[tb.contractAddress.toLowerCase()]);
    if (missingMeta.length > 0) {
      console.log(`[WalletContext] Fetching ${missingMeta.length} tokens via direct contract read`);
      try {
        const provider = await createChainProvider(chainId);
        await Promise.allSettled(missingMeta.map(async tb => {
          try {
            const contract = new ethers.Contract(tb.contractAddress, ERC20_ABI, provider);
            const [dec, sym, nm] = await Promise.all([
              contract.decimals(), contract.symbol(), contract.name()
            ]);
            const meta = { decimals: dec, symbol: sym, name: nm, logo: null };
            allMetadata[tb.contractAddress.toLowerCase()] = meta;
            cacheMetadata(tb.contractAddress, chainId, meta);
            console.log(`[WalletContext] Contract fallback OK: ${sym} (${dec} decimals)`);
          } catch (e) {
            console.warn(`[WalletContext] Contract read failed for ${tb.contractAddress}:`, e.message);
          }
        }));
      } catch (e) {
        console.warn('[WalletContext] Provider creation failed for contract fallback:', e.message);
      }
    }

    // Process tokens with metadata
    const tokens = [];

    for (const tokenBalance of nonZeroBalances) {
      const metadata = allMetadata[tokenBalance.contractAddress.toLowerCase()];

      if (!metadata) {
        console.warn(`[WalletContext] No metadata for token ${tokenBalance.contractAddress}`);
        continue;
      }

      const decimals = metadata.decimals != null ? metadata.decimals : 18;
      const symbol = metadata.symbol || 'UNKNOWN';

      // Convert hex balance to decimal
      const balanceFormatted = parseFloat(
        ethers.utils.formatUnits(tokenBalance.tokenBalance, decimals)
      );

      // Filter out dust balances and spam tokens
      const isDust = balanceFormatted < 0.000001;
      const isSpam = (
        symbol.toLowerCase().includes('claim') ||
        symbol.toLowerCase().includes('visit') ||
        symbol.toLowerCase().includes('airdrop') ||
        symbol.includes('http') ||
        symbol.includes('.com') ||
        symbol.includes('.net') ||
        symbol.includes('t.me') ||
        symbol.includes('www.') ||
        symbol.includes('|') ||
        symbol.includes('$') ||
        /[^\x00-\x7F]/.test(symbol)
      );

      if (balanceFormatted > 0 && !isDust && !isSpam) {
        tokens.push({
          chain: chain.name,
          chainId: chainId,
          symbol: symbol,
          address: tokenBalance.contractAddress,
          balance: balanceFormatted,
          decimals: decimals,
          type: 'erc20',
          name: metadata.name || symbol,
          logo: metadata.logo || null
        });
        console.log(`[WalletContext] ${chain.name} ${symbol}: ${balanceFormatted}`);
      } else if (isDust || isSpam) {
        console.log(`[WalletContext] Filtered ${symbol} (dust: ${isDust}, spam: ${isSpam})`);
      }
    }

    console.log(`[WalletContext] Found ${tokens.length} valid ERC-20 tokens on ${chain.name}`);
    return tokens;
  } catch (error) {
    console.error(`[WalletContext] Error fetching tokens from Alchemy on ${chain.name}:`, error.message);
    return [];
  }
};


// Wallet Context Provider Component
export const WalletProvider = ({ children }) => {
  // State
  const [state, setState] = useState({
    address: null,
    chainId: null,
    connected: false,
    holdings: [],
    prices: {},
    loading: false,
    error: null,
    provider: null,
    signer: null,
    walletSince: null,
  });

  // Auto-reconnect using the wallet the user last selected.
  // No savedRdns = no auto-reconnect (require manual connect via picker — avoids picking wrong wallet).
  useEffect(() => {
    const savedRdns = localStorage.getItem('xyr_eip6963_rdns');
    if (!savedRdns) return; // first visit or unknown preference — require manual connect

    if (savedRdns === '__injected__') {
      // User last connected via legacy window.ethereum (single wallet, no EIP-6963 conflict)
      const injected = getInjectedProvider();
      if (injected) {
        injected.request({ method: 'eth_accounts' })
          .then(accounts => { if (accounts.length > 0) connectWallet(true); })
          .catch(() => {});
      }
      return;
    }

    // EIP-6963 specific wallet — find it by RDNS, never fall back to window.ethereum
    const collected = new Map();
    const onAnnounce = (e) => {
      const { info, provider } = e.detail || {};
      if (info?.rdns && provider) collected.set(info.rdns, provider);
    };
    window.addEventListener('eip6963:announceProvider', onAnnounce);
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    setTimeout(() => {
      window.removeEventListener('eip6963:announceProvider', onAnnounce);
      const rawProvider = collected.get(savedRdns);
      if (!rawProvider) {
        console.warn('[WalletContext] Saved wallet RDNS not found via EIP-6963 — require manual reconnect');
        return;
      }
      rawProvider.request({ method: 'eth_accounts' })
        .then(accounts => { if (accounts.length > 0) connectWallet(true, rawProvider); })
        .catch(() => {});
    }, 300);
  }, []);

  // Auto-refresh balances every 30 seconds (matches XYR pattern)
  useEffect(() => {
    if (!state.connected || !state.address) return;

    const refreshInterval = setInterval(() => {
      console.log('[WalletContext] Auto-refreshing balances...');
      refreshBalances();
    }, 10 * 60 * 1000); // 10 minutes — Alchemy free tier burns out at 30s

    return () => clearInterval(refreshInterval);
  }, [state.connected, state.address]);

  // Connect Wallet — accepts an optional EIP-6963 injectedProvider; falls back to window.ethereum
  const connectWallet = async (silent = false, injectedProvider = null) => {
    try {
      const rawProvider = injectedProvider || getInjectedProvider();
      if (!rawProvider) {
        throw new Error('No wallet detected. Please install MetaMask or another Web3 wallet.');
      }

      if (!silent) setState(prev => ({ ...prev, loading: true, error: null }));

      const method = silent ? 'eth_accounts' : 'eth_requestAccounts';
      const accounts = await rawProvider.request({ method });
      if (!accounts || accounts.length === 0) {
        setState(prev => ({ ...prev, loading: false }));
        return null;
      }

      const provider = new ethers.providers.Web3Provider(rawProvider);
      const signer = provider.getSigner();
      const address = await signer.getAddress();
      const network = await provider.getNetwork();
      const chainId = network.chainId;

      console.log(`[WalletContext] Connected: ${address} on chain ${chainId}`);

      setState(prev => ({ ...prev, address, chainId, connected: true, provider, signer, loading: false }));

      sessionStorage.setItem('xyr_wallet_session', JSON.stringify({
        address, chainId, expires: Date.now() + 72 * 60 * 60 * 1000
      }));

      // Setup event listeners on the chosen provider
      rawProvider.removeListener('accountsChanged', handleAccountsChanged);
      rawProvider.removeListener('chainChanged', handleChainChanged);
      rawProvider.on('accountsChanged', handleAccountsChanged);
      rawProvider.on('chainChanged', handleChainChanged);

      await refreshBalances(address, provider);
      return { address, chainId };
    } catch (error) {
      console.error('[WalletContext] Connection error:', error);
      setState(prev => ({ ...prev, loading: false, error: error.message || 'Failed to connect wallet' }));
      throw error;
    }
  };

  // Disconnect Wallet — keep xyr_eip6963_rdns so the next reload reconnects the same wallet
  const disconnectWallet = () => {
    console.log('[WalletContext] Disconnecting wallet...');
    // Detach from the same provider connectWallet attached to. Removing from a
    // bare window.ethereum is a no-op whenever another extension owns it, which
    // leaks a listener pair on every connect/disconnect cycle.
    const injectedForCleanup = getInjectedProvider();
    if (injectedForCleanup) {
      injectedForCleanup.removeListener('accountsChanged', handleAccountsChanged);
      injectedForCleanup.removeListener('chainChanged', handleChainChanged);
    }
    clearAgents();                       // security: purge any HL agent trading keys on disconnect
    setState(prev => ({
      ...prev,
      address: null, chainId: null, connected: false,
      holdings: [], prices: {}, provider: null, signer: null, error: null
    }));
    clearSession();
  };

  // Event Handlers
  const handleAccountsChanged = (accounts) => {
    console.log('[WalletContext] Accounts changed:', accounts);
    if (accounts.length === 0) {
      disconnectWallet();
    } else {
      // Reconnect with new account
      window.location.reload();
    }
  };

  const handleChainChanged = (chainId) => {
    console.log('[WalletContext] Chain changed:', chainId);
    // Reload page on chain change
    window.location.reload();
  };


  // Fetch holdings across all chains
  const fetchAllHoldings = async (address = null) => {
    const targetAddress = address || state.address;

    if (!targetAddress) {
      console.warn('[WalletContext] No address for holdings fetch');
      return [];
    }

    console.log(`[WalletContext] Fetching holdings across all chains for ${targetAddress}...`);
    const holdings = [];

    try {
      // Fetch holdings from each supported chain in parallel
      const chainPromises = Object.keys(CHAINS).map(async (chainId) => {
        const chainIdNum = parseInt(chainId);
        const chainHoldings = [];

        try {
          // Create provider for this chain
          const provider = await createChainProvider(chainIdNum);
          const chain = CHAINS[chainIdNum];

          // Fetch native currency balance (ETH)
          try {
            const balance = await provider.getBalance(targetAddress);
            const balanceFormatted = parseFloat(ethers.utils.formatEther(balance));

            console.log(`[WalletContext] ${chain.name} ETH balance: ${balanceFormatted}`);

            if (balanceFormatted > 0.00001) { // Only include if balance > 0.00001 ETH (lowered threshold)
              chainHoldings.push({
                chain: chain.name,
                chainId: chainIdNum,
                symbol: chain.nativeCurrency.symbol,
                address: null, // Native currency has no address
                balance: balanceFormatted,
                decimals: 18,
                type: 'native',
                logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png'  // ETH logo from CoinGecko
              });
            }
          } catch (error) {
            console.error(`[WalletContext] Error fetching native balance on ${chain.name}:`, error.message);
          }

          // Fetch ERC-20 tokens: run Alchemy AND Blockscout in parallel, merge results
          try {
            const [alchemyTokens, blockscoutTokens] = await Promise.allSettled([
              fetchAllTokensFromAlchemy(targetAddress, chainIdNum),
              fetchTokensFromBlockscout(targetAddress, chainIdNum)
            ]);

            const alchemy = alchemyTokens.status === 'fulfilled' ? alchemyTokens.value : [];
            const blockscout = blockscoutTokens.status === 'fulfilled' ? blockscoutTokens.value : [];

            // Merge: blockscout is source of truth for addresses it finds, alchemy fills the rest
            const seen = new Set();
            const merged = [];

            // Blockscout first (has exchange_rate prices)
            for (const t of blockscout) {
              const key = (t.address || t.symbol).toLowerCase();
              if (!seen.has(key)) { seen.add(key); merged.push(t); }
            }
            // Add any alchemy tokens not already in blockscout
            for (const t of alchemy) {
              const key = (t.address || t.symbol).toLowerCase();
              if (!seen.has(key)) { seen.add(key); merged.push(t); }
            }

            console.log(`[WalletContext] ${chain.name}: ${alchemy.length} Alchemy + ${blockscout.length} Blockscout = ${merged.length} merged tokens`);
            chainHoldings.push(...merged);
          } catch (error) {
            console.error(`[WalletContext] Error fetching ERC-20 tokens on ${chain.name}:`, error.message);
          }

          console.log(`[WalletContext] Found ${chainHoldings.length} holdings on ${chain.name}`);
          return chainHoldings;
        } catch (error) {
          console.error(`[WalletContext] Error fetching holdings on chain ${chainIdNum}:`, error);
          return [];
        }
      });

      // Wait for all chains to complete — 12s per-chain timeout so a hanging chain (e.g. Optimism 429) doesn't block the others
      const chainNames = Object.values(CHAINS).map(c => c.name);
      const timedChainPromises = chainPromises.map((p, i) =>
        Promise.race([
          p,
          new Promise(res => setTimeout(() => {
            console.warn(`[WalletContext] Chain ${chainNames[i]} timed out after 12s — skipping`);
            res([]);
          }, 12000))
        ])
      );
      const allChainHoldings = await Promise.all(timedChainPromises);

      // Flatten results
      for (const chainHoldings of allChainHoldings) {
        holdings.push(...chainHoldings);
      }

      console.log(`[WalletContext] Total holdings found: ${holdings.length}`);
      console.log('[WalletContext] Holdings breakdown:', holdings.map(h => `${h.symbol} on ${h.chain}`));
      return holdings;
    } catch (error) {
      console.error('[WalletContext] Holdings fetch error:', error);
      return [];
    }
  };

  // Get Token Prices from CoinGecko using contract addresses (OPTIMIZED with parallel fetching)
  const getTokenPrices = async (holdings) => {
    try {
      const prices = {};

      // Group tokens by chain
      const ethereumTokens = holdings.filter(h => h.chainId === 1 && h.address);
      const optimismTokens = holdings.filter(h => h.chainId === 10 && h.address);
      const baseTokens     = holdings.filter(h => h.chainId === 8453 && h.address);
      const arbitrumTokens = holdings.filter(h => h.chainId === 42161 && h.address);

      console.log(`[WalletContext] Fetching prices for ${ethereumTokens.length} ETH, ${optimismTokens.length} OP, ${baseTokens.length} BASE, ${arbitrumTokens.length} ARB tokens`);
      const priceStart = Date.now();

      // Fetch ETH price + all token prices in PARALLEL
      const pricePromises = [];

      // Native ETH price
      pricePromises.push(
        cgProxy('simple/price', { ids: 'ethereum', vs_currencies: 'usd', include_24hr_change: true })
        .then(response => {
          if (response.data.ethereum) {
            prices['ETH'] = {
              usd: response.data.ethereum.usd,
              change24h: response.data.ethereum.usd_24h_change || 0
            };
          }
        }).catch(error => {
          console.warn('[WalletContext] ETH price fetch failed:', error.message);
        })
      );

      // Batch token prices (CoinGecko supports up to 250 addresses per request)
      const batchSize = 100;

      for (let i = 0; i < ethereumTokens.length; i += batchSize) {
        const batch = ethereumTokens.slice(i, i + batchSize);
        const addresses = batch.map(t => t.address).join(',');

        pricePromises.push(
          cgProxy('simple/token_price/ethereum', { contract_addresses: addresses, vs_currencies: 'usd', include_24hr_change: true })
          .then(response => {
            batch.forEach(token => {
              const priceData = response.data[token.address.toLowerCase()];
              if (priceData && priceData.usd) {
                prices[token.symbol] = {
                  usd: priceData.usd,
                  change24h: priceData.usd_24h_change || 0
                };
              }
            });
          }).catch(error => {
            console.warn(`[WalletContext] ETH batch price fetch failed:`, error.message);
          })
        );
      }

      // Batch Optimism token prices
      for (let i = 0; i < optimismTokens.length; i += batchSize) {
        const batch = optimismTokens.slice(i, i + batchSize);
        const addresses = batch.map(t => t.address).join(',');

        pricePromises.push(
          cgProxy('simple/token_price/optimistic-ethereum', { contract_addresses: addresses, vs_currencies: 'usd', include_24hr_change: true })
          .then(response => {
            batch.forEach(token => {
              const priceData = response.data[token.address.toLowerCase()];
              if (priceData && priceData.usd) {
                prices[token.symbol] = {
                  usd: priceData.usd,
                  change24h: priceData.usd_24h_change || 0
                };
              }
            });
          }).catch(error => {
            console.warn(`[WalletContext] OP batch price fetch failed:`, error.message);
          })
        );
      }

      // Batch Base token prices
      for (let i = 0; i < baseTokens.length; i += batchSize) {
        const batch = baseTokens.slice(i, i + batchSize);
        const addresses = batch.map(t => t.address).join(',');

        pricePromises.push(
          cgProxy('simple/token_price/base', { contract_addresses: addresses, vs_currencies: 'usd', include_24hr_change: true })
          .then(response => {
            batch.forEach(token => {
              const priceData = response.data[token.address.toLowerCase()];
              if (priceData && priceData.usd) {
                prices[token.symbol] = {
                  usd: priceData.usd,
                  change24h: priceData.usd_24h_change || 0
                };
              }
            });
          }).catch(error => {
            console.warn(`[WalletContext] BASE batch price fetch failed:`, error.message);
          })
        );
      }

      // Batch Arbitrum token prices
      for (let i = 0; i < arbitrumTokens.length; i += batchSize) {
        const batch = arbitrumTokens.slice(i, i + batchSize);
        const addresses = batch.map(t => t.address).join(',');

        pricePromises.push(
          cgProxy('simple/token_price/arbitrum-one', { contract_addresses: addresses, vs_currencies: 'usd', include_24hr_change: true })
          .then(response => {
            batch.forEach(token => {
              const priceData = response.data[token.address.toLowerCase()];
              if (priceData && priceData.usd) {
                prices[token.symbol] = {
                  usd: priceData.usd,
                  change24h: priceData.usd_24h_change || 0
                };
              }
            });
          }).catch(error => {
            console.warn(`[WalletContext] ARB batch price fetch failed:`, error.message);
          })
        );
      }

      // Wait for ALL price fetches in parallel (no sequential delays)
      await Promise.allSettled(pricePromises);

      // Stablecoin fallback — CoinGecko free tier frequently returns empty for USDC/USDT on newer chains
      const STABLECOIN_PRICE = 1.00;
      const STABLECOINS = new Set(['USDC', 'USDC.e', 'USDbC', 'USDT', 'USDT.e', 'DAI', 'BUSD', 'FRAX', 'LUSD', 'cUSDC', 'aUSDC']);
      for (const h of holdings) {
        if (STABLECOINS.has(h.symbol) && !prices[h.symbol]) {
          prices[h.symbol] = { usd: STABLECOIN_PRICE, change24h: 0 };
        }
      }

      console.log(`[WalletContext] ⏱️ All prices fetched in ${Date.now() - priceStart}ms`);
      console.log('[WalletContext] Prices fetched for', Object.keys(prices).length, 'tokens');
      return prices;
    } catch (error) {
      console.error('[WalletContext] Price fetch error:', error);

      // Try to load cached prices
      const cached = localStorage.getItem('xyr_wallet_prices_cache');
      if (cached) {
        try {
          const { prices, timestamp } = JSON.parse(cached);
          const age = Date.now() - timestamp;

          // Use cache if less than 5 minutes old
          if (age < 5 * 60 * 1000) {
            console.log('[WalletContext] Using cached prices (age: ' + Math.round(age / 1000) + 's)');
            return prices;
          }
        } catch (e) {
          console.error('[WalletContext] Cache parse error:', e);
        }
      }

      return {};
    }
  };

  // Refresh Holdings and Prices
  const refreshBalances = async (address = null, provider = null, clearCache = false) => {
    try {
      const startTime = Date.now();
      console.log('[WalletContext] ⏱️ Starting balance refresh...');

      // Clear stale holdings immediately so UI shows loading instead of old data
      if (clearCache) {
        Object.keys(CHAINS).forEach(chainId => {
          localStorage.removeItem(`xyr_token_metadata_${chainId}`);
        });
        localStorage.removeItem('xyr_wallet_prices_cache');
        console.log('[WalletContext] Cleared localStorage caches');
        setState(prev => ({ ...prev, holdings: [], prices: {}, loading: true }));
      }

      // Fetch holdings first
      const holdingsStart = Date.now();
      const holdings = await fetchAllHoldings(address);
      console.log(`[WalletContext] ⏱️ Holdings fetched in ${Date.now() - holdingsStart}ms`);

      // Commit holdings immediately so UI can render even if price fetch fails
      setState(prev => ({ ...prev, holdings, loading: true }));

      // Then fetch prices based on holdings
      const pricesStart = Date.now();
      let prices = {};
      try {
        prices = await getTokenPrices(holdings);
        console.log(`[WalletContext] ⏱️ Prices fetched in ${Date.now() - pricesStart}ms`);
      } catch (priceErr) {
        console.warn('[WalletContext] Price fetch failed, showing holdings without prices:', priceErr.message);
      }

      // Cache prices in localStorage
      localStorage.setItem('xyr_wallet_prices_cache', JSON.stringify({
        prices,
        timestamp: Date.now()
      }));

      // Fill in prices from Blockscout exchange_rate for any token CoinGecko missed
      holdings.forEach(h => {
        if (h.blockscoutPrice && !prices[h.symbol]) {
          prices[h.symbol] = { usd: h.blockscoutPrice, change24h: 0 };
        }
      });

      // Fetch wallet first tx date — cached in localStorage, only hits Alchemy once per wallet
      const _addr = (address || state.address || '').toLowerCase();
      const _txCacheKey = `xyr_first_tx_${_addr}`;
      const _txCached = localStorage.getItem(_txCacheKey);
      if (_txCached) {
        setState(prev => ({ ...prev, walletSince: new Date(_txCached) }));
      } else {
        fetchWalletFirstTxDate(_addr).then(date => {
          if (date) {
            localStorage.setItem(_txCacheKey, date.toISOString());
            setState(prev => ({ ...prev, walletSince: date }));
          }
        });
      }

      setState(prev => ({ ...prev, holdings, prices, loading: false }));
      console.log(`[WalletContext] ⏱️ Total refresh time: ${Date.now() - startTime}ms`);
      return { holdings, prices };
    } catch (error) {
      console.error('[WalletContext] Refresh error:', error);
      setState(prev => ({ ...prev, holdings: [], prices: {}, loading: false }));
      return { holdings: [], prices: {} };
    }
  };

  // Verify Token Holdings (for token gating)
  const verifyTokenHoldings = async (requiredToken, minAmount) => {
    if (!requiredToken || !minAmount) {
      console.log('[WalletContext] No token gate configured');
      return true; // No token gate = allow access
    }

    if (!state.connected || !state.address) {
      console.log('[WalletContext] Wallet not connected');
      return false;
    }

    try {
      console.log(`[WalletContext] Verifying token holdings: ${requiredToken}, min: ${minAmount}`);

      // Check holdings for matching token address (across all chains)
      const matchingHoldings = state.holdings.filter(
        h => h.address && h.address.toLowerCase() === requiredToken.toLowerCase()
      );

      // Sum balances across all chains for this token
      const totalBalance = matchingHoldings.reduce((sum, holding) => sum + holding.balance, 0);

      console.log(`[WalletContext] Total token balance: ${totalBalance}, required: ${minAmount}`);
      return totalBalance >= minAmount;
    } catch (error) {
      console.error('[WalletContext] Token verification error:', error);
      return false;
    }
  };

  // Get Session (for compatibility with Firebase getSession pattern)
  const getSession = async () => {
    if (state.connected && state.address) {
      return {
        address: state.address,
        chainId: state.chainId,
        connected: true
      };
    }
    return null;
  };

  // Clear Session
  const clearSession = () => {
    sessionStorage.removeItem('xyr_wallet_session');
    localStorage.removeItem('xyr_wallet_prices_cache');
  };

  // Context value
  const value = {
    ...state,
    connectWallet,
    disconnectWallet,
    refreshBalances,
    verifyTokenHoldings,
    getSession
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

export default WalletContext;
